import { Link } from "react-router-dom";
import { Image } from "@/components/ui/image";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

type Neighbourhood = {
  name: string;
  district: string;
  href: string;
  blurb: string;
  image: string;
  alt: string;
};

const PLACES: Neighbourhood[] = [
  {
    name: "Kensington",
    district: "Royal Borough · W8",
    href: "/kensington-secondary-glazing",
    blurb: "Stucco terraces and white communal gardens, hushed against Kensington High Street.",
    image: "/lovable-uploads/kensington_townhouse_sash_windows.jpg",
    alt: "White stucco Kensington townhouse with sash windows",
  },
  {
    name: "Chelsea",
    district: "RBKC · SW3",
    href: "/chelsea-secondary-glazing-guide",
    blurb: "Red-brick Victorian elegance on the Cadogan Estate, silenced from the King's Road.",
    image: "/lovable-uploads/bedroom_secondary_glazing.png",
    alt: "Red-brick Chelsea house with period sash windows",
  },
  {
    name: "Marylebone",
    district: "Howard de Walden · W1",
    href: "/marylebone-secondary-glazing-guide",
    blurb: "Georgian townhouses around Harley Street and Wigmore Hall — discreetly transformed.",
    image: "/lovable-uploads/marylebone_georgian_sash_windows.jpg",
    alt: "Georgian Marylebone townhouse facade",
  },
  {
    name: "Westminster",
    district: "City of Westminster · SW1",
    href: "/westminster-secondary-glazing",
    blurb: "Listed mansion blocks and Smith Square mews, conservation-area approved throughout.",
    image: "/lovable-uploads/combination_secondary_glazing.jpg",
    alt: "Westminster mansion block with classical windows",
  },
];

export const HeritageNeighbourhoodGrid = () => {
  return (
    <section className="bg-[#f7f5f0] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-14">
          <p className="font-playfair italic text-amber-700/80 tracking-[0.25em] uppercase text-xs mb-4">
            Local Authority
          </p>
          <h2 className="font-playfair text-3xl md:text-5xl text-stone-900 font-light leading-tight">
            Protecting London's Heritage
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed">
            Conservation-area approved installations across the city's most prestigious postcodes —
            sympathetic to listed facades, invisible from the street.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLACES.map((p) => (
            <Link
              key={p.name}
              to={p.href}
              className="group relative block bg-white border border-stone-200/70 overflow-hidden rounded-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]"
            >
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={p.image}
                  alt={p.alt}
                  width={400}
                  height={256}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/55 via-transparent to-transparent" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-stone-700 border border-stone-200/70">
                  <ShieldCheck size={11} className="text-amber-700" />
                  Conservation Area Approved
                </span>
              </div>
              <div className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">{p.district}</p>
                <h3 className="font-playfair text-2xl text-stone-900 leading-tight">{p.name}</h3>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-3">{p.blurb}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-amber-800 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  See Local Projects <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeritageNeighbourhoodGrid;